import { useMemo, useState } from 'react';
import {
  DEFAULT_AUDIT_FILTERS,
  DEFAULT_AUDIT_PER_PAGE,
} from '../constants/auditConstants';
import {
  useHistoricalActions,
  useHistoricalActionModules,
} from '../../../hooks/useHistoricalActions';

const getDateWithTime = (date, timeSuffix) => {
  if (!date) return undefined;
  return `${date}${timeSuffix}`;
};

export const useAuditLogs = () => {
  const [filters, setFilters] = useState({ ...DEFAULT_AUDIT_FILTERS });
  const [queryFilters, setQueryFilters] = useState({ ...DEFAULT_AUDIT_FILTERS });
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(DEFAULT_AUDIT_PER_PAGE);
  const [selectedAction, setSelectedAction] = useState(null);

  const queryParams = useMemo(() => {
    const params = {
      page,
      per_page: perPage,
    };

    if (queryFilters.moduleKey) params.module_key = queryFilters.moduleKey;
    if (queryFilters.userUuid) params.user_uuid = queryFilters.userUuid;
    if (queryFilters.httpMethod) params.http_method = queryFilters.httpMethod;
    if (queryFilters.search) params.search = queryFilters.search;
    if (queryFilters.endpoint) params.endpoint = queryFilters.endpoint;
    if (queryFilters.startDate) params.start_date = getDateWithTime(queryFilters.startDate, 'T00:00:00');
    if (queryFilters.endDate) params.end_date = getDateWithTime(queryFilters.endDate, 'T23:59:59');

    return params;
  }, [queryFilters, page, perPage]);

  const modulesQuery = useHistoricalActionModules();
  const actionsQuery = useHistoricalActions(queryParams, {
    enabled: !!modulesQuery.data,
    refetchInterval: 5000,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: true,
  });

  const total = actionsQuery.data?.total ?? 0;
  const effectivePerPage = actionsQuery.data?.per_page ?? perPage;
  const totalPages = Math.max(1, Math.ceil(total / effectivePerPage) || 1);

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
  };

  const handleResetFilters = () => {
    setFilters({ ...DEFAULT_AUDIT_FILTERS });
    setQueryFilters({ ...DEFAULT_AUDIT_FILTERS });
    setPage(1);
    setPerPage(DEFAULT_AUDIT_PER_PAGE);
  };

  const handleSearch = () => {
    setQueryFilters({ ...filters });
    setPage(1);
  };

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  const handlePerPageChange = (event) => {
    const value = Number(event.target.value) || DEFAULT_AUDIT_PER_PAGE;
    setPerPage(value);
    setPage(1);
  };

  const handleSelectAction = (action) => {
    setSelectedAction(action);
  };

  const handleCloseAction = () => {
    setSelectedAction(null);
  };

  return {
    modules: modulesQuery.data ?? [],
    modulesLoading: modulesQuery.isLoading,
    filters,
    page,
    perPage,
    total,
    totalPages,
    actions: actionsQuery.data?.items ?? [],
    isLoading: actionsQuery.isLoading,
    isFetching: actionsQuery.isFetching,
    error: actionsQuery.error,
    handleFilterChange,
    handleResetFilters,
    handleSearch,
    handlePageChange,
    handlePerPageChange,
    selectedAction,
    handleSelectAction,
    handleCloseAction,
    refetch: actionsQuery.refetch,
    queryParams,
  };
};
